/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, alpha, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import SearchInput from '../../components/inputs/searchInput';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import debounce from 'lodash.debounce';
import Popup from '../../components/popups/popup';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import { Link } from 'react-router-dom';
import DriverService from '../../services/driverService';
import SupervisorDropdown from '../../components/dropdowns/supervisorDropdown';
import CityDropdown from '../../components/dropdowns/cityDropdown';
import StatusDropdown from '../../components/dropdowns/statusDropdown';
import DeleteDriverModal from '../../components/modals/driver/deleteDriverModal';
import ViewDriverModal from '../../components/modals/driver/viewDriverModal';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';
import { formatPrice } from '../../utils';
import PdfDriverList from '../../components/prints/pdfDriverList';

const Drivers = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState({});
    const [totalWallet, setTotalWallet] = useState(0);
    const [totalRemainingAmount, setTotalRemainingAmount] = useState(0);

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedSupervisor, setSelectedSupervisor] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (driver) => {
        setSelectedDriver(driver);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (driver) => {
        setSelectedDriver(driver);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedBranch(null);
            setSelectedCity(null);
            setSelectedSupervisor(null);
            setSelectedStatus('');
            await fetchDrivers();
        }
    };

    const handleStatus = (status) => {
        if (status === t("active")) return "active";
        else if (status === t("inactive")) return "inactive";
    };

    const fetchDrivers = async (page = 1, name = '', branchId = null, cityId = null, supervisorId = null, status = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                name, branchId, cityId, supervisorId,
                status: handleStatus(status),
                deleted,
                ...sortState
            };
            const data = await DriverService.fetch(page, filters);
            const { drivers, totalWallet, totalRemainingAmount } = data;
            setTotalWallet(totalWallet);
            setTotalRemainingAmount(totalRemainingAmount);

            if (drivers.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setDrivers([]);
                }
            } else {
                setDrivers((prevs) => (page === 1 ? drivers : [...prevs, ...drivers]));
            }
        } catch (error) {
            console.error('Error fetching drivers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchDrivers = useMemo(() =>
        debounce((name, branchId, cityId, supervisorId, status) => {
            setPage(1);
            setHasMore(true);
            fetchDrivers(1, name, branchId, cityId, supervisorId, status);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchDrivers(value, selectedBranch?.id, selectedCity?.id, selectedSupervisor?.id, selectedStatus);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setSelectedBranch(null);
        setSelectedCity(null);
        setSelectedSupervisor(null);
        setSelectedStatus('');
        setHasMore(true)
        if (page !== 1) setPage(1);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchDrivers(page, searchTerm, selectedBranch?.id, selectedCity?.id, selectedSupervisor?.id, selectedStatus);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchDrivers(1, searchTerm, selectedBranch?.id, selectedCity?.id, selectedSupervisor?.id, selectedStatus);
    }, [selectedBranch, selectedCity, selectedSupervisor, selectedStatus, sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchDrivers(page + 1, searchTerm, selectedBranch?.id, selectedCity?.id, selectedSupervisor?.id, selectedStatus);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteDriverModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} driver={selectedDriver} />
                <ViewDriverModal open={openView} handleClose={handleCloseView} driverId={selectedDriver.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Totals row */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'end'
            }}>
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        height: 60,
                        width: isTablet ? '100%' : '50%'
                    }}
                >
                    <Box width={'33%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 12 : 16 }}>
                            {t('totalWallet')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalWallet)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box width={'50%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 12 : 16 }}>
                            {t('totalRemainingAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalRemainingAmount)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: '17%'
                        }}>
                        <PdfDriverList
                            data={{
                                name: searchTerm,
                                deleted: deleted,
                                branch: selectedBranch,
                                city: selectedCity,
                                supervisor: selectedSupervisor,
                                status: selectedStatus
                            }} />
                    </Box>
                </Box>
            </Box>

            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        mb: 2,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,

                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'90%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>

                    </Box>
                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <CityDropdown
                            isFilter={true}
                            selectedCity={selectedCity}
                            setSelectedCity={setSelectedCity}
                        />
                    </Box>
                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <BranchDropdown
                            isFilter={true}
                            selectedBranch={selectedBranch}
                            setSelectedBranch={setSelectedBranch}
                            cityId={selectedCity && selectedCity.id}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <SupervisorDropdown
                            isFilter={true}
                            selectedSupervisor={selectedSupervisor}
                            setSelectedSupervisor={setSelectedSupervisor}
                        />
                    </Box>
                    <Divider />
                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <StatusDropdown
                            isFilter={true}
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{
                            ml: isEn ? 3 : 0
                        }}>
                            <OnOffSwitchForDeleted
                                checkDeleted={deleted}
                                setCheckDeleted={setDeleted} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>



                </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'18%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <CityDropdown
                                isFilter={true}
                                selectedCity={selectedCity}
                                setSelectedCity={setSelectedCity}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                                cityId={selectedCity && selectedCity.id}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <SupervisorDropdown
                                isFilter={true}
                                selectedSupervisor={selectedSupervisor}
                                setSelectedSupervisor={setSelectedSupervisor}
                            />

                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'20%'}>
                            <StatusDropdown
                                isFilter={true}
                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 226px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('name')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('name')}
                                    {sortState.sortBy === 'name' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('phone')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('phone')}
                                    {sortState.sortBy === 'phone' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('wallet')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('wallet')}
                                    {sortState.sortBy === 'wallet' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('remainAmount')}
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('city')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('city')}
                                    {sortState.sortBy === 'city' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('branch')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('branch')}
                                    {sortState.sortBy === 'branch' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('supervisor')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('supervisor')}
                                    {sortState.sortBy === 'supervisor' && sortIcon()}
                                </Box>
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('status')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('status')}
                                    {sortState.sortBy === 'status' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {drivers.map((driver, index) => (
                            <TableRow
                                key={driver.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{driver.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{driver.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{driver.phone}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(driver.wallet)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(driver.remainAmount)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{driver.city.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{driver.branch.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{driver.supervisor.name}</TableCell>
                                {/* status */}
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box
                                        sx={{
                                            bgcolor: alpha(
                                                driver.status === 'active' ? theme.palette.green.box : theme.palette.red.box, 0.2),
                                            width: 100,
                                            justifyContent: 'center',
                                            display: 'flex',
                                            py: 0.8,
                                            borderRadius: 0.8,
                                        }}
                                    >
                                        <Box sx={{
                                            fontWeight: 'bold',
                                            color: driver.status === 'active' ? theme.palette.green.box : theme.palette.red.box,
                                        }}>
                                            {t(driver.status)}</Box>
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(driver)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        {driver.deletedAt === null && <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }}
                                            component={Link}
                                            to={`/drivers/${driver.id}`}
                                        >
                                            <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>}
                                        {driver.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }} onClick={() => handleOpenDelete(driver)}>
                                            {driver.deletedAt === null ?
                                                <DeleteOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.red.bg }} />
                                                :
                                                <ReplayOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.green.bg }} />
                                            }
                                        </Box>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Drivers;
